<script>

import Vue from 'vue'
import lodash from 'lodash'
import { DatePicker } from 'element-ui'
import { formatDate, getWeekNumber, parseDate } from 'element-ui/src/utils/date-util'
const DEFAULT_FORMATS = {
  date: 'yyyy-MM-dd',
  month: 'yyyy-MM',
  datetime: 'yyyy-MM-dd HH:mm:ss',
  time: 'HH:mm:ss',
  week: 'yyyywWW',
  timerange: 'HH:mm:ss',
  daterange: 'yyyy-MM-dd',
  monthrange: 'yyyy-MM',
  datetimerange: 'yyyy-MM-dd HH:mm:ss',
  year: 'yyyy'
}

const DATE_FORMATTER = function(value, format) {
  if (format === 'timestamp') return value.getTime()
  return formatDate(value, format)
}
const DATE_PARSER = function(text, format) {
  if (format === 'timestamp') return new Date(Number(text))
  return parseDate(text, format)
}
const RANGE_FORMATTER = function(value, format) {
  if (Array.isArray(value) && value.length === 2) {
    const start = value[0]
    const end = value[1]

    if (start && end) {
      return [DATE_FORMATTER(start, format), DATE_FORMATTER(end, format)]
    }
  }
  return ''
}
const RANGE_PARSER = function(array, format, separator) {
  if (!Array.isArray(array)) {
    array = array.split(separator)
  }
  if (array.length === 2) {
    const range1 = array[0]
    const range2 = array[1]

    return [DATE_PARSER(range1, format), DATE_PARSER(range2, format)]
  }
  return []
}
const TYPE_VALUE_RESOLVER_MAP = {
  default: {
    formatter(value) {
      if (!value) return ''
      return '' + value
    },
    parser(text) {
      if (text === undefined || text === '') return null
      return text
    }
  },
  week: {
    formatter(value, format) {
      const week = getWeekNumber(value)
      const month = value.getMonth()
      const trueDate = new Date(value)
      if (week === 1 && month === 11) {
        trueDate.setHours(0, 0, 0, 0)
        trueDate.setDate(trueDate.getDate() + 3 - (trueDate.getDay() + 6) % 7)
      }
      let date = formatDate(trueDate, format)

      date = /WW/.test(date)
        ? date.replace(/WW/, week < 10 ? '0' + week : week)
        : date.replace(/W/, week)
      return date
    },
    parser(text, format) {
      // parse as if a normal date
      return TYPE_VALUE_RESOLVER_MAP.date.parser(text, format)
    }
  },
  date: {
    formatter: DATE_FORMATTER,
    parser: DATE_PARSER
  },
  datetime: {
    formatter: DATE_FORMATTER,
    parser: DATE_PARSER
  },
  daterange: {
    formatter: RANGE_FORMATTER,
    parser: RANGE_PARSER
  },
  monthrange: {
    formatter: RANGE_FORMATTER,
    parser: RANGE_PARSER
  },
  datetimerange: {
    formatter: RANGE_FORMATTER,
    parser: RANGE_PARSER
  },
  timerange: {
    formatter: RANGE_FORMATTER,
    parser: RANGE_PARSER
  },
  time: {
    formatter: DATE_FORMATTER,
    parser: DATE_PARSER
  },
  month: {
    formatter: DATE_FORMATTER,
    parser: DATE_PARSER
  },
  year: {
    formatter: DATE_FORMATTER,
    parser: DATE_PARSER
  },
  number: {
    formatter(value) {
      if (!value) return ''
      return '' + value
    },
    parser(text) {
      const result = Number(text)

      if (!isNaN(text)) {
        return result
      } else {
        return null
      }
    }
  },
  dates: {
    formatter(value, format) {
      return value.map(date => DATE_FORMATTER(date, format))
    },
    parser(value, format) {
      return (typeof value === 'string' ? value.split(', ') : value)
        .map(date => date instanceof Date ? date : DATE_PARSER(date, format))
    }
  }
}
export default {
  name: 'CargoDateTimePicker',
  extends: DatePicker,
  computed: {
    mergedPickerOptions() {
      if (this.type === 'date') {
        let isDisabled = false
        const pickerOptions = this.pickerOptions || {}
        // 检查 disabledDate 是否存在
        if (pickerOptions.hasOwnProperty('disabledDate') && pickerOptions.disabledDate) {
          const disabledDateMethod = pickerOptions.disabledDate
          if (disabledDateMethod instanceof Function) {
            // 调用 disabledDate 方法
            isDisabled = disabledDateMethod(new Date())
          }
        }
        const shortcuts = []
        if (!isDisabled) {
          shortcuts.push({
            text: this.$t('Message.DateToday'),
            onClick: (picker) => {
              picker.$emit('pick', new Date())
            }
          })
        }

        return lodash.assignIn({}, this.pickerOptions, {
          shortcuts: shortcuts
        })
      }
      return this.pickerOptions
    }
  },
  methods: {
    mountPicker() {
      this.picker = new Vue(this.panel).$mount()
      this.picker.defaultValue = this.defaultValue
      this.picker.defaultTime = this.defaultTime
      this.picker.popperClass = this.type === 'date' ? 'cargo-date-time-picker' : this.popperClass
      this.popperElm = this.picker.$el
      this.picker.width = this.reference.getBoundingClientRect().width
      this.picker.showTime = this.type === 'datetime' || this.type === 'datetimerange'
      this.picker.selectionMode = this.selectionMode
      this.picker.unlinkPanels = this.unlinkPanels
      this.picker.arrowControl = this.arrowControl || this.timeArrowControl || false
      this.$watch('format', (format) => {
        this.picker.format = format
      })

      const updateOptions = () => {
        const options = this.mergedPickerOptions

        if (options && options.selectableRange) {
          let ranges = options.selectableRange
          const parser = TYPE_VALUE_RESOLVER_MAP.datetimerange.parser
          const format = DEFAULT_FORMATS.timerange

          ranges = Array.isArray(ranges) ? ranges : [ranges]
          this.picker.selectableRange = ranges.map(range => parser(range, format, this.rangeSeparator))
        }

        for (const option in options) {
          if (options.hasOwnProperty(option) &&
            // 忽略 time-picker 的该配置项
            option !== 'selectableRange') {
            this.picker[option] = options[option]
          }
        }

        // main format must prevail over undocumented pickerOptions.format
        if (this.format) {
          this.picker.format = this.format
        }
      }
      updateOptions()
      this.unwatchPickerOptions = this.$watch('mergedPickerOptions', () => updateOptions(), { deep: true })
      this.$el.appendChild(this.picker.$el)
      this.picker.resetView && this.picker.resetView()

      this.picker.$on('dodestroy', this.doDestroy)
      this.picker.$on('pick', (date = '', visible = false) => {
        this.userInput = null
        this.pickerVisible = this.picker.visible = visible
        this.emitInput(date)
        this.picker.resetView && this.picker.resetView()
      })

      this.picker.$on('select-range', (start, end, pos) => {
        if (this.refInput.length === 0) return
        if (!pos || pos === 'min') {
          this.refInput[0].setSelectionRange(start, end)
          this.refInput[0].focus()
        } else if (pos === 'max') {
          this.refInput[1].setSelectionRange(start, end)
          this.refInput[1].focus()
        }
      })
    },
    // 重写父类方法
    handleKeydown(event) {
      const keyCode = event.keyCode

      // ESC
      if (keyCode === 27) {
        this.pickerVisible = false
        event.stopPropagation()
        return
      }

      // Tab
      if (keyCode === 9) {
        if (!this.ranged) {
          this.handleChange()
          this.pickerVisible = this.picker.visible = false
          this.blur()
          event.stopPropagation()
        } else {
          // user may change focus between two input
          setTimeout(() => {
            if (this.refInput.indexOf(document.activeElement) === -1) {
              this.pickerVisible = false
              this.blur()
              event.stopPropagation()
            }
          }, 0)
        }
        return
      }

      // Enter
      if (keyCode === 13) {
        if (this.userInput === '' || this.isValidValue(this.parseString(this.displayValue)) || this.matchMonthAndDay()) {
          this.handleChange()
          this.pickerVisible = this.picker.visible = false
          this.blur()
        }
        event.stopPropagation()
        return
      }

      // if user is typing, do not let picker handle key input
      if (this.userInput) {
        event.stopPropagation()
        return
      }

      // delegate other keys to panel
      if (this.picker && this.picker.handleKeydown) {
        this.picker.handleKeydown(event)
      }
    },
    matchMonthAndDay() {
      if (this.userInput && this.type === 'date') {
        const userInput = this.userInput.trim()
        const monthDay = userInput.split('-')
        if (monthDay.length === 2) {
          const month = Number(monthDay[0])
          const day = Number(monthDay[1])
          if (month >= 0 && month % 1 === 0 && day >= 0) {
            return true
          }
        }
      }
      return false
    },
    // 重写父类方法
    handleChange() {
      if (this.userInput) {
        const text = this.matchMonthAndDay() ? new Date().getFullYear() + '-' + this.userInput.trim() : this.displayValue
        const value = this.parseString(text)
        if (value) {
          if (this.picker) {
            this.picker.value = value
          }
          if (this.isValidValue(value)) {
            this.emitInput(value)
            this.userInput = null
          }
        }
      }
      if (this.userInput === '') {
        this.emitInput(null)
        this.emitChange(null)
        this.userInput = null
      }
    },
    // 重写emitInput方式，如果没有传时间格式，默认：yyyy-MM-dd HH:mm:ss
    emitInput(val) {
      const formatted = this.formatToValue(val)
      if (!this.valueEquals(this.value, formatted)) {
        let date = formatted
        if (this.type === 'date' && !this.valueFormat) {
          date = formatDate(date, DEFAULT_FORMATS.datetime)
        }
        this.$emit('input', date)
      }
    },
    // 因为无法引用到picker.vue中valueEquals方法，所以要把vue中valueEquals方法复制一份
    valueEquals(a, b) {
      const dateEquals = function(a, b) {
        const aIsDate = a instanceof Date
        const bIsDate = b instanceof Date
        if (aIsDate && bIsDate) {
          return a.getTime() === b.getTime()
        }
        if (!aIsDate && !bIsDate) {
          return a === b
        }
        return false
      }

      const aIsArray = a instanceof Array
      const bIsArray = b instanceof Array
      if (aIsArray && bIsArray) {
        if (a.length !== b.length) {
          return false
        }
        return a.every((item, index) => dateEquals(item, b[index]))
      }
      if (!aIsArray && !bIsArray) {
        return dateEquals(a, b)
      }
      return false
    }
  }
}
</script>
<style lang="scss">
.cargo-date-time-picker {
    &.has-sidebar {
      width: auto!important;
      .el-picker-panel{
        &__body-wrapper{
          width: auto;
        }
        &__sidebar{
          display: flex;
          justify-content: space-around;
          width: 100%;
          top:36px;
          bottom: 281px;
          padding-top: 0;
          background: transparent;
          overflow: hidden;
          button {
            display:inline-block;
            width: auto;
          }
        }
        &__body{
          margin-left: 0!important;
        }
      }
    }
    &.has-time{
      .el-picker-panel {
        &__sidebar{
          top:82px;
          bottom: 281px;
        }
        &__body{
          .el-date-picker__editor-wrap{
            width: 147px;
          }
        }
      }
    }
}
</style>
